import { getProxyUrl } from '@proxies/index'
import { BackAcabadosInfo } from '@utils/conversion/getStandardMaterial'

export type CatalogTextureRecord = BackAcabadosInfo

export type StoredTextureLike = {
  zoneName?: string
  zoneDisplayName?: string
  zone?: string
  linkedTextureID?: number
  idAcabadoUnidadModeloAcabado?: string
  searchID?: number
  id?: number
  groupID?: number
  name?: string
  description?: string
  descripcion?: string
  url?: string
  acabado?: string
  modelo?: string
}

const catalogTexturesCache = new Map<number, CatalogTextureRecord[]>()

export function clearCatalogTexturesCache(): void {
  catalogTexturesCache.clear()
}

export async function safeParseJsonResponse(response: Response): Promise<unknown> {
  if (response.status === 204) return null

  const text = await response.text()
  if (!text.trim()) return null

  try {
    return JSON.parse(text)
  } catch (error) {
    console.warn('[textureCatalogApi] invalid JSON response', {
      status: response.status,
      url: response.url,
      bodyPreview: text.slice(0, 120),
      error,
    })
    return null
  }
}

function normalizeUrl(url: string): string {
  return url.trim().toLowerCase().replace(/\?.*$/, '')
}

function normalizeLabel(value: string | undefined): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
}

function parseCatalogTexturesResponse(payload: unknown): CatalogTextureRecord[] {
  if (Array.isArray(payload)) return payload as CatalogTextureRecord[]
  if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: CatalogTextureRecord[] }).data
  }
  return []
}

export async function fetchCatalogTexturesForAcabado(
  acabadoUnidadId: number,
): Promise<CatalogTextureRecord[]> {
  const cached = catalogTexturesCache.get(acabadoUnidadId)
  if (cached) return cached

  const response = await fetch(getProxyUrl(`api/Acabados/gettexturas/${acabadoUnidadId}`), {
    method: 'GET',
    mode: 'cors',
    headers: {
      'Accept-Language': 'ES',
      Authorization: 'Ninpo ' + sessionStorage.getItem('access_token'),
    },
  })

  if (!response.ok) {
    catalogTexturesCache.set(acabadoUnidadId, [])
    return []
  }

  const json = await safeParseJsonResponse(response)
  const textures = parseCatalogTexturesResponse(json)
  catalogTexturesCache.set(acabadoUnidadId, textures)
  return textures
}

export function hasCatalogTexturesCache(acabadoUnidadId: number): boolean {
  return catalogTexturesCache.has(acabadoUnidadId)
}

export function findMatchingCatalogTexture(
  applied: StoredTextureLike,
  catalog: CatalogTextureRecord[],
): CatalogTextureRecord | undefined {
  if (!catalog.length) return undefined

  const appliedUrl = applied.url ? normalizeUrl(applied.url) : ''
  const appliedName = normalizeLabel(applied.name || applied.description || applied.descripcion)

  if (appliedUrl) {
    const byUrl = catalog.find((entry) => entry.url && normalizeUrl(entry.url) === appliedUrl)
    if (byUrl) return byUrl
  }

  if (appliedName) {
    const byName = catalog.find((entry) => {
      const nombre = normalizeLabel(entry.nombre)
      const descripcion = normalizeLabel(entry.descripcion)
      return nombre === appliedName || descripcion === appliedName
    })
    if (byName) return byName
  }

  if (typeof applied.searchID === 'number' && applied.searchID > 0) {
    return catalog.find((entry) => entry.idTexturas === applied.searchID)
  }

  return undefined
}

export function normalizeTextureUrl(url: string): string {
  return normalizeUrl(url)
}
